import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { SettingKey } from '@frontend/setting/types';
import { TransactionStateName } from '@frontend/transaction/types';
import React, { useCallback, useState } from 'react';

import { useAppSelector } from '../../redux/store';
import { useLicenseRepository } from '../../utils/license-repository';
import { MoveTransactionDestination, MoveTransactionResult, isMovableTransactionState, moveTransaction } from './move-transaction';

export interface MoveTransactionParams {
    accountId: string;
    transactionId: string;
    transactionState: TransactionStateName;
    transactionUserId?: string | null;
    currentSpotId?: string | null;
    currentSlotId?: string | null;
    destination: MoveTransactionDestination;
}

export const useMoveTransaction = () => {
    const navigationState = useAppSelector((state) => state.navigation);
    const { getFunctionality, getFunctionalitySetting } = useLicenseRepository();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<React.ReactNode | null>(null);

    const moveFunctionality = getFunctionality(Application.PUDO, Feature.MOVE_TRANSACTION);
    const moveSettings = getFunctionalitySetting(moveFunctionality?.id ?? '');

    const dropOffPackageWorkflowId = moveSettings?.[SettingKey.DROP_OFF_PACKAGE_WORKFLOW_ID] as string | undefined;
    const pickUpPackageWorkflowId = moveSettings?.[SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID] as string | undefined;

    const canMove = !!navigationState.user && !!moveFunctionality && !!dropOffPackageWorkflowId && !!pickUpPackageWorkflowId;

    const executeMove = useCallback(
        async (params: MoveTransactionParams): Promise<MoveTransactionResult | null> => {
            if (!canMove || isSubmitting || !dropOffPackageWorkflowId || !pickUpPackageWorkflowId) {
                setError(CommonMessage.ERRORS.DEFAULT);
                return null;
            }
            if (!isMovableTransactionState(params.transactionState)) {
                setError(CommonMessage.ERRORS.DEFAULT);
                return null;
            }

            setIsSubmitting(true);
            setError(null);
            try {
                const result = await moveTransaction({
                    ...params,
                    dropOffPackageWorkflowId,
                    pickUpPackageWorkflowId,
                    actorUserId: navigationState.user?.id,
                    triggerLocationSpotId: navigationState.spot?.id
                });
                return result;
            } catch {
                setError(CommonMessage.ERRORS.DEFAULT);
                return null;
            } finally {
                setIsSubmitting(false);
            }
        },
        [canMove, isSubmitting, dropOffPackageWorkflowId, pickUpPackageWorkflowId, navigationState.user?.id, navigationState.spot?.id]
    );

    return {
        canMove,
        isSubmitting,
        error,
        setError,
        executeMove
    };
};
